import React, { useState } from 'react';
import type { AdAccount, Page, Payer, Pixel, WizardTarget } from './useLaunchWizard.ts';

type Props = {
  accounts: AdAccount[];
  pages: Page[];
  targets: Record<string, WizardTarget>;
  pixels: Record<string, Pixel[]>;
  payers: Record<string, Payer[]>;
  selectedOrder: string[];
  onSelect: (id: string, yes: boolean) => void;
  onUpdate: (id: string, patch: Partial<WizardTarget>) => void;
  onBulkSelect: (ids: string[]) => void;
  loadPixels: (id: string) => void;
  loadPayers: (id: string) => void;
  templateBudget: number | null;
};

function BulkAccsModal({ accounts, onAdd, onClose }: { accounts: AdAccount[]; onAdd: (ids: string[]) => void; onClose: () => void }) {
  const [text, setText] = useState('');

  const handleAdd = () => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    const found: string[] = [];
    for (const line of lines) {
      // strip act_ prefix if present
      const clean = line.replace(/^act_/, '');
      const match = accounts.find((a) => a.id === clean || a.id === line);
      if (match) found.push(match.id);
    }
    if (found.length) onAdd(found);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bulk add accounts</h3>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>✕</button>
        </div>
        <p className="text-muted text-sm">Paste ad account IDs — one per line. They will be auto-selected.</p>
        <textarea
          className="input modal-textarea"
          placeholder={'123456789\n987654321\n...'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <div className="modal-actions">
          <button className="btn btn--secondary btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={handleAdd}>Add</button>
        </div>
      </div>
    </div>
  );
}

export function CabinetsStep({ accounts, pages, targets, pixels, payers, selectedOrder, templateBudget, onSelect, onUpdate, onBulkSelect, loadPixels, loadPayers }: Props) {
  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(() => sessionStorage.getItem('launch-only-selected') === 'true');
  const [showBulkModal, setShowBulkModal] = useState(false);

  const setOnlySelectedWithStorage = (val: boolean) => {
    setOnlySelected(val);
    sessionStorage.setItem('launch-only-selected', String(val));
  };

  // Build a map for O(1) lookup
  const accountMap = Object.fromEntries(accounts.map((a) => [a.id, a]));

  // Determine which accounts to show, in the correct order.
  // Selected accounts appear first — in the order they were selected.
  // Unselected accounts follow, in backend order (alphabetical).
  let visible: AdAccount[];
  if (onlySelected) {
    // Only show selected, in selection order
    visible = selectedOrder.map((id) => accountMap[id]).filter(Boolean);
  } else {
    // Show selected first (in selection order), then remaining unselected accounts
    const selectedSet = new Set(selectedOrder);
    const unselected = accounts.filter((a) => !selectedSet.has(a.id));
    const orderedSelected = selectedOrder.map((id) => accountMap[id]).filter(Boolean);
    visible = [...orderedSelected, ...unselected];
  }

  // Apply search filter
  if (search) {
    const q = search.toLowerCase();
    visible = visible.filter((a) => `${a.id} ${a.name ?? ''}`.toLowerCase().includes(q));
  }

  const handleBulkAdd = (ids: string[]) => {
    onBulkSelect(ids);
    setOnlySelectedWithStorage(true);
  };

  return (
    <div className="wizard-step">
      {showBulkModal && (
        <BulkAccsModal
          accounts={accounts}
          onAdd={handleBulkAdd}
          onClose={() => setShowBulkModal(false)}
        />
      )}

      <div className="inline-fields">
        <input className="input" placeholder="Search ID or account name" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="toggle">
          <input
            type="checkbox"
            checked={onlySelected}
            onChange={(e) => setOnlySelectedWithStorage(e.target.checked)}
          /> Only selected
        </label>
        <button className="btn btn--outline btn--sm" onClick={() => setShowBulkModal(true)}>⚡ Bulk Accs</button>
      </div>

      <div className="wizard-table">
        <div className="wizard-row wizard-row--header">
          <span></span>
          <span>Ad account</span>
          <span>Fan Page</span>
          <span>Pixel</span>
          <span>Link</span>
          <span>Budget</span>
          <span>Additional</span>
        </div>

        {visible.map((account) => {
          const target = targets[account.id];
          return (
            <div className="wizard-row" key={account.id}>
              <label>
                <input type="checkbox" checked={Boolean(target)} onChange={(e) => onSelect(account.id, e.target.checked)} />
              </label>
              <div>
                <strong>{account.name ?? account.id}</strong>
                <small>{account.id} · {account.currency ?? '—'}</small>
              </div>

              {target && (
                <>
                  <select className="input" value={target.page_id} onChange={(e) => onUpdate(account.id, { page_id: e.target.value, post_id: '' })}>
                    <option value="">Fan Page</option>
                    {pages.map((page) => <option value={page.id} key={page.id}>{`${page.name ?? page.id} (${page.id})`}</option>)}
                  </select>

                  <select
                    className={`input ${!target.pixel_id ? 'input--invalid' : ''}`}
                    value={target.pixel_id ?? ''}
                    onFocus={() => loadPixels(account.id)}
                    onChange={(e) => onUpdate(account.id, { pixel_id: e.target.value || null })}
                  >
                    <option value="">
                      {pixels[account.id]
                        ? (pixels[account.id].length > 0 ? 'Select Pixel (required)' : 'No pixels found')
                        : 'Loading Pixels…'}
                    </option>
                    {(pixels[account.id] ?? []).map((pixel) => (
                      <option value={pixel.id} key={pixel.id}>{`${pixel.name ?? pixel.id} (${pixel.id})`}</option>
                    ))}
                  </select>

                  <input className="input" placeholder="Link (optional)" value={target.link_url} onChange={(e) => onUpdate(account.id, { link_url: e.target.value })} />

                  <input className="input" type="number" placeholder={templateBudget ? `Budget (${templateBudget})` : 'Budget'} value={target.budget_override ?? ''} onChange={(e) => onUpdate(account.id, { budget_override: e.target.value ? Number(e.target.value) : null })} />

                  <div className="payer-fields">
                    <select
                      className="input"
                      onFocus={() => loadPayers(account.id)}
                      onChange={(e) => {
                        const payer = (payers[account.id] ?? [])[Number(e.target.value)];
                        if (payer) onUpdate(account.id, { beneficiary_name: payer.beneficiaryName ?? payer.payerName ?? '', payer_name: payer.beneficiaryName ?? payer.payerName ?? '' });
                      }}
                    >
                      <option>Saved beneficiary</option>
                      {(payers[account.id] ?? []).map((payer, i) => <option key={i} value={i}>{payer.beneficiaryName ?? payer.payerName}</option>)}
                    </select>
                    <input className="input" placeholder="Beneficiary / payer" value={target.beneficiary_name} onChange={(e) => onUpdate(account.id, { beneficiary_name: e.target.value, payer_name: e.target.value })} />
                    <textarea className="input" placeholder="URL tags" value={target.url_tags} onChange={(e) => onUpdate(account.id, { url_tags: e.target.value })} />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
